import React, { useEffect, useRef, useState } from "react";
import { useParams, useLocation, useNavigationType } from "react-router-dom";
import Scroll3DAnimation from "./Scroll3DAnimation";
import Footer from "./Footer";
import LoadingSlider from "./LoadingSlider";
import { useGetProductListData, useSetProductChangeCall } from "./service";
import useSocket from "../hooks/useSocketMessage";
import io from "socket.io-client";

const Home = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigationType = useNavigationType();

  const [showLoading, setShowLoading] = useState(false);
  const [forceLoading, setForceLoading] = useState(true);
  const [sectionPositions, setSectionPositions] = useState([]);
  const [scrollTarget, setScrollTarget] = useState(null);

  const hasModelLoadedOnce = useRef(false);

  const defaultUrl = process.env.REACT_APP_DEFAULT_URL;
  const finalId =
    id === undefined && location.pathname === "/home"
      ? defaultUrl
      : id || defaultUrl;

  const { data } = useGetProductListData(finalId);
  const { mutate: variantChange } = useSetProductChangeCall();

  const socketRef = useRef(null);
  const URL = process.env.REACT_APP_SOCKET_URL;

  if (!socketRef.current) {
    socketRef.current = io(URL, { autoConnect: false });
  }

  const socket = socketRef.current;
  const { currProductKey, isLoadingScreen } = useSocket(socket);

  const sessionID = data?.data?.sessionID;
  const scrollData = data?.data?.scroll;

  // ✅ On first mount, store the scroll target from location.state
  const scrollIntent = location.state?.scrollToSection;
  useEffect(() => {
    if (scrollIntent) {
      setScrollTarget(scrollIntent.toLowerCase());
      window.history.replaceState(null, "", window.location.pathname); // Clean URL
    }
  }, [scrollIntent]);

  // ✅ Once sectionPositions are available, scroll to the target
  useEffect(() => {
    if (!scrollTarget || sectionPositions.length === 0) return;

    const match = sectionPositions.find(
      (s) =>
        s.text?.toLowerCase().replace(/\s+/g, "") ===
        scrollTarget.replace(/\s+/g, "")
    );

    if (match) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: match.top, behavior: "smooth" });
      });
      setScrollTarget(null); // prevent repeat scroll
    }
  }, [scrollTarget, sectionPositions]);

  useEffect(() => {
    hasModelLoadedOnce.current = false;
    setForceLoading(true);
    const timer = setTimeout(() => {
      setForceLoading(false);
    }, 100);
    return () => clearTimeout(timer);
  }, [id]);

  useEffect(() => {
    if (sessionID) {
      socket.auth = { sessionId: sessionID };
      socket.disconnect();
      socket.connect();
    }
  }, [id, sessionID]);

  const isFirstMount = useRef(true);
  useEffect(() => {
    if (
      isFirstMount.current &&
      location.pathname === "/home" &&
      (navigationType === "POP" || navigationType === "REPLACE")
    ) {
      setShowLoading(true);
      const timer = setTimeout(() => {
        setShowLoading(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
    isFirstMount.current = false;
  }, [location.pathname, navigationType]);

  useEffect(() => {
    if (!isLoadingScreen && currProductKey) {
      hasModelLoadedOnce.current = true;
    }
  }, [isLoadingScreen, currProductKey]);

  if (
    showLoading ||
    forceLoading ||
    (isLoadingScreen && !hasModelLoadedOnce.current)
  ) {
    return <LoadingSlider />;
  }

  const innerMargin = scrollData?.frames?.margin || 0;
  const maxWidth = scrollData?.frames?.max_width || 1280;
  const innerWidth = window.innerWidth;

  let blankMargin = 0;
  let sumMargin = 0;
  let pageWidthCalc = 0;
  let newMaxWidth = 0;

  if (innerWidth > maxWidth) {
    newMaxWidth = maxWidth;
    blankMargin = (innerWidth - newMaxWidth) / 2;
    sumMargin = 2 * innerMargin * newMaxWidth;
    pageWidthCalc = newMaxWidth - sumMargin;
  } else {
    newMaxWidth = innerWidth;
    blankMargin = 0;
    sumMargin = 2 * innerMargin * innerWidth;
    pageWidthCalc = innerWidth - sumMargin;
  }

  const footerData = scrollData?.frames?.footer;
  const headerData = scrollData?.frames?.header;
  const footerPosition = blankMargin + sumMargin / 2;
  const productData = scrollData?.products;

  const detectScreenType = () => {
    const frames = scrollData?.frames;
    const screen_type = frames?.screen_type;
    const aspectRatio = window.innerWidth / window.innerHeight;
    const screenWidth = window.innerWidth;

    let selectedScreenType = screen_type?.find(
      (screen) => aspectRatio >= parseFloat(screen.aspect_ratio_lower_limit)
    );
    if (!selectedScreenType) {
      selectedScreenType = screen_type?.find((screen) => screen.is_default);
    }

    return {
      selectedScreenTypeId: selectedScreenType?.screen_type_id || "02",
      screenWidthFactor: selectedScreenType
        ? screenWidth / parseFloat(selectedScreenType.standard_width)
        : 1,
    };
  };

  const { selectedScreenTypeId } = detectScreenType();

  const getDimensions = (data, selectedScreenTypeId) => {
    const widthObj = data?.width?.find(
      (w) => w.screen_type_id === selectedScreenTypeId
    );
    const heightObj = data?.height?.find(
      (h) => h.screen_type_id === selectedScreenTypeId
    );

    return {
      width: widthObj ? `${parseFloat(widthObj.width) * 100}vw` : "100vw",
      height: heightObj ? `${parseFloat(heightObj.height) * 100}vh` : "auto",
    };
  };

  const footerDimensions = getDimensions(footerData, selectedScreenTypeId);
  const HeaderDimensions = getDimensions(headerData, selectedScreenTypeId);

  const handleVariantChange = (variant, propertyId) => {
    const payload = {
      session_id: sessionID,
      message: {
        type: "change_variant",
        message: {
          product_id: productData?.[0]?.product_id,
          property_id: propertyId,
          variant_id: variant,
        },
      },
    };
    variantChange(payload);
  };

  return (
    <>
      <Scroll3DAnimation
        HeaderDimensions={HeaderDimensions}
        data={data}
        isLoadingScreen={isLoadingScreen}
        onSectionPositionsReady={setSectionPositions}
      />

      {footerData?.is_visible && (
        <Footer
          footerDimensions={footerDimensions}
          pageWidthCalc={pageWidthCalc}
          footerPosition={footerPosition}
        />
      )}
    </>
  );
};

export default Home;
